import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/seo/SEO';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calculator, CloudRain, ClipboardCheck, PenTool, Sprout, ArrowRight, FileCheck2 } from 'lucide-react';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';

const tools = [
    {
        title: "Sample Inspection",
        desc: "Review common BMPs in a guided field workflow before creating an account.",
        href: "/demo/sample-inspection",
        icon: FileCheck2,
        color: "text-emerald-600",
        bg: "bg-emerald-100 dark:bg-emerald-900/30"
    },
    {
        title: "Rain Event Finder",
        desc: "Retroactively check if your jobsite had a >0.5\" rain event in the last 30 days.",
        href: "/tools/rain-event-finder",
        icon: CloudRain,
        color: "text-blue-600",
        bg: "bg-blue-100 dark:bg-blue-900/30"
    },
    {
        title: "Fine Risk Calculator",
        desc: "Estimate potential EPA civil penalties based on site duration and inspection frequency.",
        href: "/tools/fine-calculator",
        icon: Calculator,
        color: "text-red-600",
        bg: "bg-red-100 dark:bg-red-900/30"
    },
    {
        title: "Map Sketcher",
        desc: "Upload a site plan and drag-and-drop SWPPP BMP symbols (Silt Fence, Inlets, etc).",
        href: "/tools/map-sketcher",
        icon: PenTool,
        color: "text-indigo-600",
        bg: "bg-indigo-100 dark:bg-indigo-900/30"
    },
    {
        title: "Runoff Calculator",
        desc: "Calculate the weighted 'C' value (Runoff Coefficient) for your hydrology analysis.",
        href: "/tools/runoff-calculator",
        icon: Calculator,
        color: "text-teal-600",
        bg: "bg-teal-100 dark:bg-teal-900/30"
    },
    {
        title: "SWPPP Auditor",
        desc: "Self-audit checklist to ensure your SWPPP binder meets EPA Chapter 2 requirements.",
        href: "/tools/swppp-audit",
        icon: ClipboardCheck,
        color: "text-orange-600",
        bg: "bg-orange-100 dark:bg-orange-900/30"
    },
    {
        title: "70% Density Visualizer",
        desc: "Visual reference guide for Notice of Termination (NOT) stabilization requirements.",
        href: "/resources/vegetation-density",
        icon: Sprout,
        color: "text-green-600",
        bg: "bg-green-100 dark:bg-green-900/30"
    }
];

const ToolsIndexPage: React.FC = () => {
    // AEO Optimization: ItemList Schema
    // Helps Google understand this page is a collection of distinct software applications
    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": "Free SWPPP Compliance Tools",
        "description": "A collection of calculators and utilities for construction stormwater management.",
        "itemListElement": tools.map((tool, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "url": `https://runofftrack.com${tool.href}`,
            "name": tool.title,
            "description": tool.desc
        }))
    };

    return (
        <div className="page-shell">
            <SEO
                title="Free SWPPP Compliance Tools & Calculators"
                description="Suite of free tools for environmental consultants: Rain Event Finder, Fine Calculator, Map Sketcher, and Runoff Coefficient Calculator."
                canonical="/tools"
                jsonLd={jsonLd}
            />

            <div className="page-intro">
                <div className="container relative z-10 mx-auto max-w-4xl px-4 text-center">
                    <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Tools', href: '/tools' }]} />
                    
                    <p className="section-label mb-3 mt-4">Free field utilities</p>
                    <h1 className="mb-6 text-4xl font-black tracking-[-0.04em] md:text-6xl">
                        Compliance Toolkit
                    </h1>
                    <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                        Utility-first tools to help you manage stormwater inspections, calculate risks, and prepare reports. No login required.
                    </p>
                </div>
            </div>

            <div className="container px-4 py-16 max-w-6xl mx-auto">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {tools.map((tool) => (
                        <Link key={tool.href} to={tool.href} className="group h-full">
                            <Card className="flex h-full flex-col transition-[border-color,box-shadow,transform] hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-lg">
                                <CardHeader>
                                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                                        <tool.icon size={24} />
                                    </div>
                                    <CardTitle className="text-xl group-hover:text-primary transition-colors">{tool.title}</CardTitle>
                                </CardHeader>
                                <CardContent className="flex-grow">
                                    <p className="text-muted-foreground leading-relaxed">
                                        {tool.desc}
                                    </p>
                                </CardContent>
                                <CardFooter>
                                    <div className="text-sm font-medium text-primary flex items-center gap-2 group-hover:underline underline-offset-4">
                                        Open Tool <ArrowRight size={14} />
                                    </div>
                                </CardFooter>
                            </Card>
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ToolsIndexPage;
